import { useCallback, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { Warning } from "@phosphor-icons/react";

export const AI_CONFIRM_DEFAULT =
  "You are about to send private or confidential information to AI. " +
  "The submission is fingerprinted in your entity's AI submission log. " +
  "Please confirm this is what you intend to do.";

export const AI_CONFIRM_NATF =
  "Only upload a blank NATF questionnaire, or one whose responses the vendor has posted publicly. " +
  "Do NOT upload a privately completed questionnaire containing a vendor's confidential responses. " +
  "By confirming, you affirm this file contains no private or confidential information (or that the " +
  "vendor published it) and its contents will be sent to AI. The submission is fingerprinted in your " +
  "entity's AI submission log.";

// Promise-based confidential-AI confirmation. Usage:
//   const { confirmAi, aiConfirmDialog } = useAiConfirm();
//   if (!(await confirmAi())) return;            // generic message
//   if (!(await confirmAi(AI_CONFIRM_NATF))) …   // custom message
// Render {aiConfirmDialog} once in the component tree.
export function useAiConfirm() {
  const [msg, setMsg] = useState(null);
  const resolver = useRef(null);

  const confirmAi = useCallback((message) => new Promise((resolve) => {
    resolver.current = resolve;
    setMsg(message || AI_CONFIRM_DEFAULT);
  }), []);

  const close = (ok) => {
    setMsg(null);
    if (resolver.current) { resolver.current(ok); resolver.current = null; }
  };

  const aiConfirmDialog = (
    <AlertDialog open={!!msg} onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent className="rounded-none" data-testid="ai-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-amber-600" />
            Send confidential information to AI?
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="ai-confirm-message">{msg}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" onClick={() => close(false)} data-testid="ai-confirm-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction className="rounded-none" onClick={() => close(true)} data-testid="ai-confirm-proceed">
            Yes, send to AI
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmAi, aiConfirmDialog };
}
